import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getCancellationReport, createCancellationReportDownload } from '../../../../APICalling/APICalling';
import { useGlobalContext } from '../../../../Context/GlobalContext';
import Loader from '../../../../Components/Loader/Loader';
import { toast } from 'react-toastify';
import ReportHeader from '../ReportHeader/ReportHeader';
import Placeholder from '../../../../Components/Placeholder/Placeholder';
import SearchNotFoundIcon from '../../../../Components/Icons/SearchNotFoundIcon';
import styles from './CancellationReport.module.css';
import { calculateReportDateRange, getPeriodText, isReportRangeWithinMonths } from '../../../../Utils/ReportDateUtils';
import { attachSharedUserChannelListener } from '../../../../Utils/clearSharedUserChannelEvent';
import { downloadFileWithAuth, getDownloadFilename } from '../../../../Utils/downloadFileWithAuth';

const CancellationReport = ({ filterParams }) => {
    const { selectedOutlet, userAssignedOutlets } = useGlobalContext();
    const [cancellationData, setCancellationData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [outletName, setOutletName] = useState('');
    const [periodText, setPeriodText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [pagination, setPagination] = useState(null);
    const [expandedBooking, setExpandedBooking] = useState(null);
    const [bookingDetails, setBookingDetails] = useState({});
    const [loadingDetails, setLoadingDetails] = useState({});
    const [downloadLoading, setDownloadLoading] = useState(false);
    const observer = useRef();
    const fetchCancellationReportRef = useRef();

    // Get outlet name
    useEffect(() => {
        if (selectedOutlet && userAssignedOutlets?.length) {
            const outlet = userAssignedOutlets.find(o => o.id === selectedOutlet);
            setOutletName(outlet?.name || 'Unknown');
        }
    }, [selectedOutlet, userAssignedOutlets]);

    const formatDateTimeForDisplay = (dateTimeString) => {
        if (!dateTimeString) return 'N/A';
        const date = new Date(dateTimeString);
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            year: 'numeric'
        });
    };

    const formatTimeForDisplay = (dateTimeString) => {
        if (!dateTimeString) return 'N/A';
        const date = new Date(dateTimeString);
        return date.toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: false
        });
    };

    // 24-hour time for range display
    const formatTime24 = (dateTimeString) => {
        if (!dateTimeString) return 'N/A';
        const date = new Date(dateTimeString);
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
    };

    const fetchCancellationReport = useCallback(async (page = 1, isInitialLoad = false) => {
        try {
            if (isInitialLoad) {
                setLoading(true);
            } else {
                setLoadingMore(true);
            }

            // Calculate dates using utility function
            const { startDate, endDate } = calculateReportDateRange(filterParams);

            const params = {
                start_date: startDate,
                end_date: endDate,
                outlet_id: filterParams.outlet || selectedOutlet,
                per_page: 100,
                pagination: 1,
                page: page,
                archive: 1
            };

            // Add therapist filter if provided
            // Handle empty array (when all therapists are unselected)
            if (filterParams.staff !== undefined) {
                if (Array.isArray(filterParams.staff)) {
                    // Format array as JSON string: [42,19,20]
                    params.therapist = JSON.stringify(filterParams.staff);
                } else {
                    // If it's a string or other format, wrap in array and stringify
                    params.therapist = JSON.stringify([filterParams.staff]);
                }
            }
            if (filterParams.archive === 1) {
                params.archive = 1;
            }

            if (isInitialLoad) {
                setPeriodText(getPeriodText(filterParams));
            }

            const response = await getCancellationReport(params);

            if (response?.data?.success) {
                // API returns data in response.data.data.list structure
                const dataList = response.data?.data?.list || [];
                // Pagination is in response.data.data.pagination
                const paginationInfo = response.data?.data?.pagination;

                if (isInitialLoad) {
                    setCancellationData(dataList);
                } else {
                    setCancellationData(prev => [...prev, ...dataList]);
                }

                setPagination(paginationInfo);

                // Check if there are more pages
                if (paginationInfo && paginationInfo.currentPage >= paginationInfo.lastPage) {
                    setHasMore(false);
                } else {
                    setHasMore(true);
                }
            } else {
                if (isInitialLoad) {
                    toast.error(response?.response?.data?.message || response?.data?.message || response?.message || 'Failed to fetch cancellation report');
                    setCancellationData([]);
                }
                setHasMore(false);
            }
        } catch (error) {
            console.error('Error fetching cancellation report:', error);
            if (isInitialLoad) {
                toast.error('An error occurred while fetching cancellation report');
                setCancellationData([]);
            }
            setHasMore(false);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [filterParams, selectedOutlet]);

    // Store fetchCancellationReport in ref for use in observer
    useEffect(() => {
        fetchCancellationReportRef.current = fetchCancellationReport;
    }, [fetchCancellationReport]);

    // Trigger initial fetch when filterParams changes
    // Only call API if filterParams.outlet matches selectedOutlet (i.e., Generate was clicked)
    useEffect(() => {
        if (filterParams && filterParams.date && filterParams.outlet === selectedOutlet) {
            setCurrentPage(1);
            setCancellationData([]);
            setHasMore(true);
            fetchCancellationReport(1, true);
        }
    }, [filterParams, fetchCancellationReport, selectedOutlet]);

    // Intersection Observer for infinite scroll
    const lastItemRef = useCallback(node => {
        if (loading || loadingMore) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore && pagination) {
                const nextPage = currentPage + 1;
                if (nextPage <= pagination.lastPage && fetchCancellationReportRef.current) {
                    setCurrentPage(nextPage);
                    fetchCancellationReportRef.current(nextPage, false);
                }
            }
        });
        if (node) observer.current.observe(node);
    }, [loading, loadingMore, hasMore, pagination, currentPage]);

    const handleBookingClick = async (booking) => {
        const bookingId = booking.booking_id;

        // If already expanded, collapse it
        if (expandedBooking === bookingId) {
            setExpandedBooking(null);
            return;
        }

        // If details already loaded, just expand
        if (bookingDetails[bookingId]) {
            setExpandedBooking(bookingId);
            return;
        }

        // Load booking details
        try {
            setLoadingDetails(prev => ({ ...prev, [bookingId]: true }));

            // Calculate dates using utility function
            const { startDate, endDate } = calculateReportDateRange(filterParams);

            const params = {
                start_date: startDate,
                end_date: endDate,
                outlet_id: filterParams.outlet || selectedOutlet,
                per_page: 100,
                pagination: 1,
                booking_id: bookingId,
                archive: 1
            };

            // Add therapist filter if provided
            // Handle empty array (when all therapists are unselected)
            if (filterParams.staff !== undefined) {
                if (Array.isArray(filterParams.staff)) {
                    // Format array as JSON string: [42,19,20]
                    params.therapist = JSON.stringify(filterParams.staff);
                } else {
                    // If it's a string or other format, wrap in array and stringify
                    params.therapist = JSON.stringify([filterParams.staff]);
                }
            }
            if (filterParams.archive === 1) {
                params.archive = 1;
            }

            const response = await getCancellationReport(params);

            if (response?.data?.success) {
                // API returns data in response.data.data.list structure (same as main fetch)
                const dataList = response.data?.data?.list || [];
                if (dataList.length > 0) {
                    const details = dataList[0];
                    setBookingDetails(prev => ({
                        ...prev,
                        [bookingId]: details
                    }));
                } else {
                    // Set empty details to show no-data message instead of toast
                    setBookingDetails(prev => ({
                        ...prev,
                        [bookingId]: { booking_items: [] }
                    }));
                }
                setExpandedBooking(bookingId);
            } else {
                // Set empty details to show no-data message instead of toast
                setBookingDetails(prev => ({
                    ...prev,
                    [bookingId]: { booking_items: [] }
                }));
                setExpandedBooking(bookingId);
            }
        } catch (error) {
            console.error('Error fetching booking details:', error);
            // Set empty details to show no-data message instead of toast
            setBookingDetails(prev => ({
                ...prev,
                [bookingId]: { booking_items: [] }
            }));
            setExpandedBooking(bookingId);
        } finally {
            setLoadingDetails(prev => ({ ...prev, [bookingId]: false }));
        }
    };

    const handleDownload = async () => {
        if (!isReportRangeWithinMonths(filterParams, 3)) {
            toast.error('Please select a date range within 90 days to proceed.');
            return;
        }
        // Get user_id and token from localStorage
        const userId = localStorage.getItem('user_id');
        const token = localStorage.getItem('token');

        if (!userId) {
            toast.error('User ID not found');
            return;
        }

        if (!token) {
            toast.error('Authentication token not found');
            return;
        }

        // Create user-specific private channel name
        const channelName = `user.${userId}`;
        let channel;
        let detach = null;
        let toastId = null;

        setDownloadLoading(true);

        // Show loading toast
        toastId = toast.loading("Downloading in progress...");

        try {
            // Join shared user channel — do not Echo.leave (payroll also uses user.{id}).
            const eventName = 'CancellReportReady';

            const cleanup = () => {
                try {
                    detach?.();
                } catch (e) {}
                if (toastId) {
                    toast.dismiss(toastId);
                    toastId = null;
                }
            };

            const { channel: joinedChannel, detach: detachListener } = attachSharedUserChannelListener(
                channelName,
                eventName,
                async (event) => {
                    try {
                        const downloadUrl = event?.url || event?.data?.url;

                        if (!downloadUrl) {
                            toast.error('Download URL not received. Please try again.');
                            cleanup();
                            setDownloadLoading(false);
                            return;
                        }

                        try {
                            await downloadFileWithAuth(
                                downloadUrl,
                                getDownloadFilename(event, downloadUrl, 'cancellation-report.csv')
                            );
                            toast.success('Downloaded successfully');
                        } catch (downloadError) {
                            console.error('Download error:', downloadError);
                            toast.error('Failed to start download');
                        }

                        cleanup();
                        setDownloadLoading(false);
                    } catch (error) {
                        toast.error('Failed to process download');
                        cleanup();
                        setDownloadLoading(false);
                    }
                }
            );
            channel = joinedChannel;
            detach = detachListener;

            // Error handler
            if (typeof channel.error === 'function') {
                channel.error((error) => {
                    toast.error('Socket connection error');
                    cleanup();
                    setDownloadLoading(false);
                });
            }

            // Wait for subscription to be ready before calling API
            await new Promise((resolve) => setTimeout(resolve, 500));

            // Calculate dates using utility function
            const { startDate, endDate } = calculateReportDateRange(filterParams);

            const outlet = filterParams.outlet || selectedOutlet;

            const downloadParams = {
                start_date: startDate,
                end_date: endDate,
                outlet: outlet,
                user_id: userId
            };

            // Add therapist filter if provided
            // Handle empty array (when all therapists are unselected)
            if (filterParams.staff !== undefined) {
                if (Array.isArray(filterParams.staff)) {
                    // Format array as JSON string: [42,19,20]
                    downloadParams.therapist = JSON.stringify(filterParams.staff);
                } else {
                    // If it's a string or other format, wrap in array and stringify
                    downloadParams.therapist = JSON.stringify([filterParams.staff]);
                }
            }
            if (filterParams.archive === 1) {
                downloadParams.archive = 1;
            }

            const createResponse = await createCancellationReportDownload(downloadParams);

            const responseStatus = createResponse?.status || createResponse?.data?.status;
            const isSuccess = createResponse?.data?.data?.success ||
                createResponse?.data?.success ||
                createResponse?.success ||
                responseStatus === 'processing' ||
                responseStatus === 'success';

            if (isSuccess) {
                return; // Wait for socket event
            }

            // If not successful, handle error
            if (!isSuccess) {
                const backendError = createResponse?.data?.data?.message ||
                    createResponse?.data?.message ||
                    createResponse?.message ||
                    'Failed to initiate download';

                // Show user-friendly error message
                let userMessage = 'Failed to generate report. ';

                if (backendError.includes('S3') || backendError.includes('AWS') || backendError.includes('storage')) {
                    userMessage += 'Server storage configuration issue. Please contact support.';
                } else if (backendError.includes('ACL')) {
                    userMessage += 'File permission error. Please contact support.';
                } else {
                    userMessage = backendError.substring(0, 200);
                }

                toast.error(userMessage);
                cleanup();
                setDownloadLoading(false);
            }
        } catch (error) {
            console.error('Error downloading cancellation report:', error);
            toast.error(error?.message || 'An error occurred while exporting cancellation report');
            setDownloadLoading(false);
        }
    };

    const handleBookingIdClick = (bookingId) => {
        let url = `/sales?search=${bookingId}`;
        if (selectedOutlet) {
            url += `&outlet=${selectedOutlet}`;
        }
        window.open(url, "_blank");
    };

    return (
        <div className={styles.cancellationReportContainer}>
            {/* Header Section */}
            <ReportHeader
                outletName={outletName}
                periodText={periodText}
                onDownload={handleDownload}
                downloadLoading={downloadLoading}
                hasData={cancellationData && cancellationData.length > 0}
                downloadButtonText="Download CSV"
            />

            {/* Table Section */}
            {loading ? (
                <div className={styles.loaderContainer}>
                    <Loader />
                </div>
            ) : cancellationData && cancellationData.length > 0 ? (
                <div className={styles.tableWrapper}>
                    <table className={styles.cancellationTable}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 10, }}>
                            <tr>
                                <th className={styles.colBookingId}>Booking Id</th>
                                <th className={styles.colClient}>Client</th>
                                <th className={styles.colDate}>Appointment Date</th>
                                <th className={styles.colTime}>Appointment Time</th>
                                <th className={styles.colCancellationType}>Cancellation Type</th>
                                <th className={styles.colCreatedBy}>Created By</th>
                                <th className={styles.colCancelledBy}>Cancelled By</th>
                                <th className={styles.colCancelledDate}>Cancelled on Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {cancellationData.map((item, index) => {
                                const isLastItem = index === cancellationData.length - 1;
                                return (
                                <React.Fragment key={item.booking_id || index}>
                                    {/* Main booking row */}
                                    <tr 
                                        ref={isLastItem ? lastItemRef : null}
                                        className={`${styles.bookingRow} ${expandedBooking === item.booking_id ? styles.expanded : ''}`}
                                        onClick={() => handleBookingClick(item)}
                                    >
                                        <td className={styles.colBookingId}>
                                            {/* <span className={styles.bookingIdLink} onClick={() => handleBookingIdClick(item.booking_id)}>{item.booking_id}</span> */}
                                             <span className={styles.bookingIdLink}>{item.booking_id}</span>
                                        </td>
                                        <td className={styles.colClient}>{item.client_name || 'N/A'}</td>
                                        <td className={styles.colDate}>{formatDateTimeForDisplay(item.created_at)}</td>
                                        <td className={styles.colTime}>{formatTime24(item.created_at)}</td>
                                        <td className={styles.colCancellationType}>
                                            {item.cancellation_type === 'Cancelled'
                                                ? 'Normal cancellation'
                                                : item.cancellation_type || 'N/A'}
                                        </td>
                                        <td className={styles.colCreatedBy}>{item.created_by || '-'}</td>
                                        <td className={styles.colCancelledBy}>{item.cancelled_by || '-'}</td>
                                        <td className={styles.colCancelledDate}>{formatDateTimeForDisplay(item.cancelled_at)}</td>
                                    </tr>

                                    {/* Expanded booking details */}
                                    {expandedBooking === item.booking_id && (
                                        <tr className={styles.expandedRow}>
                                            <td colSpan="8">
                                                <div className={styles.bookingDetailsContainer}>
                                                    {loadingDetails[item.booking_id] ? (
                                                        <div className={styles.loaderSmall}>
                                                            <Loader />
                                                        </div>
                                                    ) : bookingDetails[item.booking_id]?.booking_items?.length > 0 ? (
                                                        <>
                                                            <div className={styles.bookingDetailsTitle}>
                                                                Booking Service Details
                                                            </div>
                                                            <table className={styles.serviceDetailsTable}>
                                                                <thead>
                                                                    <tr>
                                                                        <th className={styles.colServiceName}>Service Name</th>
                                                                        <th className={styles.colTherapist}>Assigned Therapist</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {bookingDetails[item.booking_id].booking_items.map((service, idx) => (
                                                                        <tr key={idx}>
                                                                            <td className={styles.colServiceName}>{service.service || 'N/A'}</td>
                                                                            <td className={styles.colTherapist}>{service.therapist || 'N/A'}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </>
                                                    ) : (
                                                        <p className={styles.noData}>No service details available</p>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                            })}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className={styles.noData}>
                    <Placeholder
                        icon={<SearchNotFoundIcon width="24" height="24" color="white"/>}
                        heading="No cancellation data found"
                        para="Please try changing the filters"
                    />
                </div>
            )}
        </div>
    );
};

export default CancellationReport;

